/**
 * Visible-time accounting — the clock every duration in this subsystem reads.
 *
 * Wall-clock time is the wrong unit for "how long was someone on this page".
 * A tab left open over a weekend is not forty-eight hours of reading, and a
 * single such tab moves a mean by an order of magnitude. This clock only
 * advances while `document.visibilityState === 'visible'`, so a backgrounded
 * tab contributes nothing.
 *
 * Both durations we report are derived from it:
 *
 * - **Per-page dwell** (`previous_dwell_ms` on `web_page_viewed`) — the
 *   difference between two marks within one page load.
 * - **Session duration** (`duration_ms` on `web_session_ended`) — the same
 *   clock, accumulated across page loads via `sessionStorage`, so a reload
 *   inside the tab does not reset it.
 *
 * Deliberately not idle-aware. Detecting that someone walked away needs
 * mousemove/keydown/scroll listeners on every page, and the cost is real
 * while the accuracy gain is speculative. The bound in
 * `config.maxDwellMs` is the cheap guard against the tail instead — see
 * `boundedDwellMs`.
 *
 * Self-initializing on import rather than waiting for `initLogging()`: the
 * clock has to be running before the first thing worth timing happens, and
 * making correctness depend on someone remembering an init order is exactly
 * the kind of latent trap this subsystem is trying not to have.
 *
 * @module logging/core/visible-clock
 */

import { loggingConfig } from '../config';
import { readStoredNumber, writeStored } from './storage';

/** Total visible ms for the session, including prior page loads. */
const VISIBLE_MS_KEY = 'homeceo_log_visible_ms';

/**
 * Visible ms accumulated in *previous* page loads of this session.
 *
 * Read once, at import, and thereafter only written by
 * `resetSessionVisibleMs`. Re-reading it from storage would double count,
 * because what gets persisted is the running total *including* this load.
 */
let priorLoadsMs = readStoredNumber(VISIBLE_MS_KEY);

/** Visible ms in this page load, excluding any run currently open. */
let closedRunsMs = 0;

/** When the current visible run began, or null while hidden. */
let runStartedAt: number | null = null;

function openRun(): void {
  if (runStartedAt === null) runStartedAt = Date.now();
}

function closeRun(): void {
  if (runStartedAt === null) return;
  closedRunsMs += Math.max(0, Date.now() - runStartedAt);
  runStartedAt = null;
}

/** Visible ms since this page load started. */
export function visibleMsThisLoad(): number {
  const openMs = runStartedAt === null ? 0 : Math.max(0, Date.now() - runStartedAt);
  return closedRunsMs + openMs;
}

/** Visible ms since the session began, across reloads within the tab. */
export function sessionVisibleMs(): number {
  return priorLoadsMs + visibleMsThisLoad();
}

/**
 * Round a duration to whole milliseconds and clamp it to `maxDwellMs`.
 *
 * Every duration that reaches the wire goes through this, so the bound is
 * applied in one place rather than being remembered at each call site.
 */
export function boundedDwellMs(rawMs: number): number {
  return Math.min(Math.round(Math.max(0, rawMs)), loggingConfig.maxDwellMs);
}

/** Persist the running total so a reload continues rather than restarts. */
function persist(): void {
  writeStored(VISIBLE_MS_KEY, String(Math.round(sessionVisibleMs())));
}

if (typeof document !== 'undefined') {
  if (document.visibilityState === 'visible') openRun();

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      openRun();
    } else {
      closeRun();
      persist();
    }
  });

  /* `pagehide` as well as `visibilitychange`, because a reload fires
     `pagehide` and may not leave time for anything else — without this,
     time spent on a page that is then refreshed is silently lost from the
     session total. Persisting twice is harmless: `persist` writes an
     absolute total, not an increment. */
  window.addEventListener('pagehide', () => {
    closeRun();
    persist();
  });
}

/**
 * Clear the session's accumulated visible time.
 *
 * Called when a new session id is minted, so a fresh session cannot inherit
 * an earlier one's clock. The in-memory `priorLoadsMs` is zeroed alongside
 * the stored value — clearing only storage would leave this load still
 * adding a total that no longer exists.
 */
export function resetSessionVisibleMs(): void {
  priorLoadsMs = 0;
  closedRunsMs = 0;
  runStartedAt = document.visibilityState === 'visible' ? Date.now() : null;
  writeStored(VISIBLE_MS_KEY, '0');
}
